import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Users, Search, RefreshCw, Plus, ChevronRight, Wallet,
  TrendingUp, Star, Shield, AlertCircle, ArrowUpCircle, ArrowDownCircle, Zap
} from 'lucide-react';
import { smartgateAPI } from '../../api';

const StatusBadge = ({ status }) => {
  const map = {
    active: 'bg-green-100 text-green-700',
    blocked: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${map[status] || 'bg-slate-100 text-slate-600'}`}>
      {status}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, color = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-xs text-slate-400">{label}</p>
        <p className="text-xl font-bold text-slate-800">{value?.toLocaleString() ?? '-'}</p>
      </div>
    </div>
  );
};

// ─── Manual Adjustment Modal ───────────────────────────────────────────────────
const AdjustModal = ({ wallet, onClose, onSave }) => {
  const [mode, setMode] = useState('credit');
  const [points, setPoints] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    const p = parseInt(points);
    if (!p || p <= 0) { toast.error('Enter a valid points value'); return; }
    setSaving(true);
    try {
      if (mode === 'credit') {
        await smartgateAPI.creditWallet(wallet.customer_id, { points: p, reason });
        toast.success(`Credited ${p} points`);
      } else {
        await smartgateAPI.debitWallet(wallet.customer_id, { points: p, reason });
        toast.success(`Debited ${p} points`);
      }
      onSave();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || 'Adjustment failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="font-bold text-slate-800 text-lg mb-1">Adjust Points</h3>
        <p className="text-sm text-slate-400 mb-5">Customer: <span className="font-mono text-slate-700">{wallet.customer_id}</span></p>

        <div className="flex gap-2 mb-4">
          {['credit', 'debit'].map(m => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-colors ${
                mode === m
                  ? m === 'credit' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {m === 'credit' ? '+ Add Points' : '- Remove Points'}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Points</label>
            <input
              type="number"
              value={points}
              onChange={e => setPoints(e.target.value)}
              min="1"
              placeholder="e.g. 50"
              data-testid="adjust-points-input"
              className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Reason (optional)</label>
            <input
              type="text"
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. Compensation, event reward..."
              data-testid="adjust-reason-input"
              className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <button onClick={onClose} className="flex-1 py-2 text-sm text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            data-testid="adjust-submit-btn"
            className={`flex-1 py-2 text-sm font-semibold text-white rounded-lg transition-colors ${
              mode === 'credit' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
            } disabled:opacity-50`}
          >
            {saving ? 'Saving...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function SmartGateWallets() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [wallets, setWallets] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [adjustWallet, setAdjustWallet] = useState(null);
  const [featureEnabled, setFeatureEnabled] = useState(false);

  const load = useCallback(async (q = search, p = page) => {
    setLoading(true);
    try {
      const [walletsRes, analyticsRes] = await Promise.all([
        smartgateAPI.listWallets({ search: q || undefined, page: p, page_size: 20 }),
        smartgateAPI.getAnalytics(),
      ]);
      setWallets(walletsRes.data.wallets || []);
      setTotal(walletsRes.data.total || 0);
      setAnalytics(analyticsRes.data);
      setFeatureEnabled(true);
    } catch (e) {
      const err = e.response?.data?.detail;
      if (err?.error === 'PLAN_UPGRADE_REQUIRED') {
        toast.error('Professional plan required for SmartGate');
        navigate('/partners/smartgate');
      } else if (err?.error === 'FEATURE_DISABLED') {
        setFeatureEnabled(false);
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, search, page]);

  useEffect(() => { load(search, page); }, [page]); // eslint-disable-line

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    load(search, 1);
  };

  const handleAdjustSave = () => load(search, page);

  if (!featureEnabled && !loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-slate-700 mb-2">SmartGate Not Enabled</h2>
        <p className="text-sm text-slate-500 mb-6">Enable SmartGate in settings to start managing customer wallets.</p>
        <button
          onClick={() => navigate('/partners/smartgate')}
          className="px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700"
          data-testid="go-to-settings-btn"
        >
          Go to Settings
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6" data-testid="smartgate-wallets-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/partners/smartgate')} className="text-slate-400 hover:text-slate-600">
            <Shield className="w-5 h-5" />
          </button>
          <ChevronRight className="w-4 h-4 text-slate-300" />
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" /> Customer Wallets
            </h1>
            <p className="text-xs text-slate-400 mt-0.5">{total} wallets total</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/partners/smartgate')}
          className="text-sm text-slate-500 hover:text-blue-600 flex items-center gap-1"
        >
          <Shield className="w-4 h-4" /> Settings
        </button>
      </div>

      {/* Analytics */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="smartgate-analytics">
          <StatCard icon={Users} label="Total Wallets" value={analytics.total_wallets} color="blue" />
          <StatCard icon={Star} label="Welcome Bonus Issued" value={analytics.welcome_bonus_issued} color="amber" />
          <StatCard icon={TrendingUp} label="Purchase Points Credited" value={analytics.purchase_rewards_credited} color="green" />
          <StatCard icon={Zap} label="Points Spent on AI" value={analytics.points_spent_on_ai} color="purple" />
        </div>
      )}

      {/* Search bar */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by customer ID, email or phone..."
            data-testid="wallet-search-input"
            className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button type="submit" className="px-4 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700">
          Search
        </button>
        <button type="button" onClick={() => { setSearch(''); setPage(1); load('', 1); }} className="px-3 py-2.5 text-slate-500 bg-slate-100 rounded-lg hover:bg-slate-200">
          <RefreshCw className="w-4 h-4" />
        </button>
      </form>

      {/* Wallets table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : wallets.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <Wallet className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No wallets found</p>
            <p className="text-sm mt-1">Wallets are auto-created when customers first use AI features.</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="wallets-table">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Customer ID</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Balance</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Earned</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Spent</th>
                <th className="text-center px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {wallets.map(w => (
                <tr key={w.customer_id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-3">
                    <span className="font-mono text-xs text-slate-700">{w.customer_id}</span>
                    {w.customer_email && <span className="block text-xs text-slate-400">{w.customer_email}</span>}
                  </td>
                  <td className="px-5 py-3 text-right font-bold text-slate-800">{w.points_balance ?? 0}</td>
                  <td className="px-5 py-3 text-right text-green-600 font-medium">{w.total_points_earned ?? 0}</td>
                  <td className="px-5 py-3 text-right text-slate-500">{w.total_points_spent ?? 0}</td>
                  <td className="px-5 py-3 text-center"><StatusBadge status={w.wallet_status} /></td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setAdjustWallet(w)}
                        title="Adjust points"
                        className="p-1.5 text-slate-400 hover:text-blue-600 rounded"
                        data-testid={`adjust-btn-${w.customer_id}`}
                      >
                        <ArrowUpCircle className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/partners/smartgate/wallets/${w.customer_id}`)}
                        title="View details"
                        className="p-1.5 text-slate-400 hover:text-blue-600 rounded"
                        data-testid={`detail-btn-${w.customer_id}`}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {total > 20 && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>Showing {(page - 1) * 20 + 1}–{Math.min(page * 20, total)} of {total}</span>
          <div className="flex gap-2">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 rounded bg-slate-100 hover:bg-slate-200 disabled:opacity-40">Prev</button>
            <button disabled={page * 20 >= total} onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 rounded bg-slate-100 hover:bg-slate-200 disabled:opacity-40">Next</button>
          </div>
        </div>
      )}

      {/* Adjust Modal */}
      {adjustWallet && (
        <AdjustModal
          wallet={adjustWallet}
          onClose={() => setAdjustWallet(null)}
          onSave={handleAdjustSave}
        />
      )}
    </div>
  );
}
